import type { MonitoringAiSettings } from './monitoringAiSettings';

/** 검출 성향 8단계 (1=가장 민감, 8=가장 보수) */
export const DETECTION_LEVEL_IDS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;
export type DetectionLevelId = (typeof DETECTION_LEVEL_IDS)[number];

export type GlobalDetectionPresetId = DetectionLevelId | 'custom';
export type PoolDetectionPresetId = DetectionLevelId | 'custom';

const LEGACY_TO_LEVEL: Record<string, DetectionLevelId> = {
  sensitive: '2',
  balanced: '5',
  conservative: '7',
};

export type GlobalPresetFields = Pick<
  MonitoringAiSettings,
  | 'min_confidence'
  | 'min_box_area_ratio'
  | 'min_box_side_px'
  | 'min_track_hits'
  | 'track_iou_threshold'
  | 'max_track_misses'
>;

const ANCHOR_GLOBAL = {
  // yolov8n(COCO) 수영장 장면 기준 — UI「신뢰도 하한」과 동일 스케일
  sensitive: {
    min_confidence: 0.28,
    min_box_area_ratio: 0.0004,
    min_box_side_px: 8,
    min_track_hits: 2,
    track_iou_threshold: 0.04,
    max_track_misses: 14,
  } satisfies GlobalPresetFields,
  balanced: {
    min_confidence: 0.30,
    min_box_area_ratio: 0.0006,
    min_box_side_px: 10,
    min_track_hits: 3,
    track_iou_threshold: 0.04,
    max_track_misses: 12,
  } satisfies GlobalPresetFields,
  conservative: {
    min_confidence: 0.42,
    min_box_area_ratio: 0.002,
    min_box_side_px: 20,
    min_track_hits: 4,
    track_iou_threshold: 0.06,
    max_track_misses: 8,
  } satisfies GlobalPresetFields,
};

const ANCHOR_POOL = {
  sensitive: { ai_detection_conf: 0.22, ai_detection_min_consecutive_hits: 2 },
  balanced: { ai_detection_conf: 0.30, ai_detection_min_consecutive_hits: 3 },
  conservative: { ai_detection_conf: 0.42, ai_detection_min_consecutive_hits: 5 },
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpGlobalFields(a: GlobalPresetFields, b: GlobalPresetFields, t: number): GlobalPresetFields {
  return {
    min_confidence: lerp(a.min_confidence, b.min_confidence, t),
    min_box_area_ratio: lerp(a.min_box_area_ratio, b.min_box_area_ratio, t),
    min_box_side_px: Math.round(lerp(a.min_box_side_px, b.min_box_side_px, t)),
    min_track_hits: Math.max(1, Math.round(lerp(a.min_track_hits, b.min_track_hits, t))),
    track_iou_threshold: lerp(a.track_iou_threshold, b.track_iou_threshold, t),
    max_track_misses: Math.max(1, Math.round(lerp(a.max_track_misses, b.max_track_misses, t))),
  };
}

function globalFieldsAtLevel(level: DetectionLevelId): GlobalPresetFields {
  const n = Number(level);
  if (n <= 5) {
    const t = (n - 1) / 4;
    return lerpGlobalFields(ANCHOR_GLOBAL.sensitive, ANCHOR_GLOBAL.balanced, t);
  }
  const t = (n - 5) / 3;
  return lerpGlobalFields(ANCHOR_GLOBAL.balanced, ANCHOR_GLOBAL.conservative, t);
}

function poolFieldsAtLevel(level: DetectionLevelId): {
  ai_detection_conf: number;
  ai_detection_min_consecutive_hits: number;
} {
  const n = Number(level);
  if (n <= 5) {
    const t = (n - 1) / 4;
    return {
      ai_detection_conf: Number(
        lerp(ANCHOR_POOL.sensitive.ai_detection_conf, ANCHOR_POOL.balanced.ai_detection_conf, t).toFixed(
          3
        )
      ),
      ai_detection_min_consecutive_hits: Math.max(
        1,
        Math.round(
          lerp(
            ANCHOR_POOL.sensitive.ai_detection_min_consecutive_hits,
            ANCHOR_POOL.balanced.ai_detection_min_consecutive_hits,
            t
          )
        )
      ),
    };
  }
  const t = (n - 5) / 3;
  return {
    ai_detection_conf: Number(
      lerp(ANCHOR_POOL.balanced.ai_detection_conf, ANCHOR_POOL.conservative.ai_detection_conf, t).toFixed(3)
    ),
    ai_detection_min_consecutive_hits: Math.max(
      1,
      Math.round(
        lerp(
          ANCHOR_POOL.balanced.ai_detection_min_consecutive_hits,
          ANCHOR_POOL.conservative.ai_detection_min_consecutive_hits,
          t
        )
      )
    ),
  };
}

export const DETECTION_LEVEL_META: Record<
  DetectionLevelId,
  { title: string; globalDescription: string; poolDescription: string }
> = {
  '1': {
    title: '1단계 · 매우 민감',
    globalDescription: '작은·먼 사람도 최대한 잡음. 오탐이 많아지기 가능.',
    poolDescription: '이 시설에서 검출을 가장 쉽게',
  },
  '2': {
    title: '2단계 · 민감',
    globalDescription: '민감 쪽에 가깝게. 물결·반사 오탐에 유의',
    poolDescription: '민감에 가깝게 적용',
  },
  '3': {
    title: '3단계 · 다소 민감',
    globalDescription: '기본보다 조금 더 잘 잡는 쪽',
    poolDescription: '다소 민감하게 적용',
  },
  '4': {
    title: '4단계 · 약간 민감',
    globalDescription: '균형보다 살짝 민감',
    poolDescription: '균형보다 약간 민감',
  },
  '5': {
    title: '5단계 · 균형 (권장)',
    globalDescription: '일반 실내 시설에 맞춘 기본값. 대부분 여기서 시작.',
    poolDescription: '권장. 전역 설정과 잘 맞음',
  },
  '6': {
    title: '6단계 · 약간 보수',
    globalDescription: '균형보다 오탐을 조금 줄',
    poolDescription: '균형보다 약간 보수적',
  },
  '7': {
    title: '7단계 · 보수',
    globalDescription: '허위 박스·경보가 많을 때. 가끔 놓치기 가능',
    poolDescription: '오탐을 더 줄',
  },
  '8': {
    title: '8단계 · 매우 보수',
    globalDescription: '오탐을 최소화. 놓침이 느기 가능',
    poolDescription: '이 시설에서 검출을 가장 엄격하게',
  },
};

/** @deprecated 레거시 키 호환 — GLOBAL_DETECTION_PRESET_LABELS 대신 DETECTION_LEVEL_META 사용 */
export const GLOBAL_DETECTION_PRESET_LABELS = Object.fromEntries(
  DETECTION_LEVEL_IDS.map((id) => [
    id,
    { title: DETECTION_LEVEL_META[id].title, description: DETECTION_LEVEL_META[id].globalDescription },
  ])
) as Record<DetectionLevelId, { title: string; description: string }>;

export const GLOBAL_DETECTION_PRESETS: Record<DetectionLevelId, GlobalPresetFields> =
  Object.fromEntries(DETECTION_LEVEL_IDS.map((id) => [id, globalFieldsAtLevel(id)])) as Record<
    DetectionLevelId,
    GlobalPresetFields
  >;

export const POOL_DETECTION_PRESET_LABELS = Object.fromEntries(
  DETECTION_LEVEL_IDS.map((id) => [
    id,
    { title: DETECTION_LEVEL_META[id].title, description: DETECTION_LEVEL_META[id].poolDescription },
  ])
) as Record<DetectionLevelId, { title: string; description: string }>;

export const POOL_DETECTION_PRESETS: Record<
  DetectionLevelId,
  { ai_detection_conf: number; ai_detection_min_consecutive_hits: number }
> = Object.fromEntries(DETECTION_LEVEL_IDS.map((id) => [id, poolFieldsAtLevel(id)])) as Record<
  DetectionLevelId,
  { ai_detection_conf: number; ai_detection_min_consecutive_hits: number }
>;

const PRESET_TOLERANCE = 0.02;

function near(a: number, b: number, tol = PRESET_TOLERANCE): boolean {
  return Math.abs(a - b) <= tol;
}

export function normalizeDetectionLevelId(raw: unknown): DetectionLevelId | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (DETECTION_LEVEL_IDS.includes(s as DetectionLevelId)) {
    return s as DetectionLevelId;
  }
  return LEGACY_TO_LEVEL[s] ?? null;
}

export function isDetectionLevelId(v: string): v is DetectionLevelId {
  return DETECTION_LEVEL_IDS.includes(v as DetectionLevelId);
}

export function inferGlobalDetectionPreset(s: MonitoringAiSettings): GlobalDetectionPresetId {
  const stored = normalizeDetectionLevelId(s.detection_preset);
  if (stored) return stored;
  if (s.detection_preset === 'custom') return 'custom';

  for (const id of DETECTION_LEVEL_IDS) {
    const p = GLOBAL_DETECTION_PRESETS[id];
    if (
      near(s.min_confidence, p.min_confidence) &&
      near(s.min_box_area_ratio, p.min_box_area_ratio, 0.0003) &&
      s.min_box_side_px === p.min_box_side_px &&
      s.min_track_hits === p.min_track_hits &&
      near(s.track_iou_threshold, p.track_iou_threshold) &&
      s.max_track_misses === p.max_track_misses
    ) {
      return id;
    }
  }
  return 'custom';
}

export function applyGlobalDetectionPreset(
  preset: DetectionLevelId,
  current: MonitoringAiSettings
): MonitoringAiSettings {
  return {
    ...current,
    ...GLOBAL_DETECTION_PRESETS[preset],
    detection_preset: preset,
  };
}

export function inferPoolDetectionPreset(
  conf: number | null | undefined,
  hits: number | null | undefined
): PoolDetectionPresetId {
  const c = Number(conf);
  const h = Number(hits);
  if (!Number.isFinite(c) || !Number.isFinite(h)) return 'custom';

  // 1~5단계는 conf 간격이 0.007~0.008이라 tol=0.02 '첫 매칭'이면
  // 5→3, 4→2, 2→1 처럼 다른 단계로 보인다. 동일 hits 중 가장 가까운 단계만 채택.
  let best: DetectionLevelId | null = null;
  let bestDist = Infinity;
  for (const id of DETECTION_LEVEL_IDS) {
    const p = POOL_DETECTION_PRESETS[id];
    if (h !== p.ai_detection_min_consecutive_hits) continue;
    const dist = Math.abs(c - p.ai_detection_conf);
    if (dist < bestDist) {
      bestDist = dist;
      best = id;
    }
  }
  // 프리셋 conf는 소수 3자리. DB float 오차만 허용.
  if (best != null && bestDist <= 0.005) return best;
  return 'custom';
}

export function applyPoolDetectionPreset(
  preset: DetectionLevelId
): { ai_detection_conf: number; ai_detection_min_consecutive_hits: number } {
  return { ...POOL_DETECTION_PRESETS[preset] };
}
